import {
  DependencyList,
  Fragment,
  FunctionComponent,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { SelectChangeEvent, ThemeProvider } from '@mui/material';
import isFunction from 'lodash/isFunction';
import keys from 'lodash/keys';
import omit from 'lodash/omit';
import size from 'lodash/size';

import { ReactComponent as UncheckedIcon } from '../../../../../assets/icons/checkbox-icons/unchecked.svg';
import Box from '../../../Box/Box';
import Checkbox from '../../../Checkbox';
import MenuItem from '../../../MenuItem/MenuItem';
import Select from '../../../Select/Select';
import TableBody from '../../../TableBody/TableBody';
import TableCell from '../../../TableCell/TableCell';
import TableContainer from '../../../TableContainer/TableContainer';
import TableHead from '../../../TableHead/TableHead';
import TablePagination from '../../../TablePagination/TablePagination';
import TableRow from '../../../TableRow/TableRow';
import TableRowExpandable from '../../../TableRowExpandable';
import { Alignments } from '../../enums/alignments.enum';
import { SortingState } from '../../enums/table-sorting-state.enum';
import { TableColumnDef } from '../../models/table-column.model';
import { TableObjectModel } from '../../models/table-object.model';
import { BaseTableProps } from '../../models/table-props.model';
import { AbstractRow } from '../../models/table-row.model';
import { TableSortingState } from '../../models/table-sorting-state.model';
import baseTableTheme from '../../theme/theme/table.theme';
import { tableGrey } from '../../theme/variables/variables';
import TableCells from '../table-cells';
import Table from './Table';
import {
  SetIsCheckedContext,
  SetIsCheckedContextModel,
} from './context/set-is-checked/set-is-checked.context';

const SDCTable: FunctionComponent<BaseTableProps> = (props): ReactElement => {
  const [sortedBy, _setIsSortedBy] = useState<TableObjectModel<SortingState>>(
    {}
  );

  const checkedCtx = useContext(
    SetIsCheckedContext
  ) as SetIsCheckedContextModel;

  const rowsFromProps: AbstractRow[] = props.rows as AbstractRow[];
  const columns: TableColumnDef[] = props.columns;

  const [expandedRows, setExpandedRows] = useState<boolean[]>(
    new Array(props.rows.length).fill(false)
  );

  const toggleExpandRow = (rowIndex: number): void => {
    setExpandedRows((prevState: boolean[]) =>
      prevState.map((item, index) => (item ? !item : index === rowIndex))
    );

    if (isFunction(props.expandRowAction) && !!props.changePrompt) {
      props.expandRowAction();
    }
  };

  const setIsSortedBy = (srt: TableSortingState<SortingState>): void => {
    if (props.sortHandler) {
      props.sortHandler(isFunction(srt) ? srt(sortedBy) : srt);
    }

    _setIsSortedBy(srt);
  };

  const bulkActionsHandler = (event: SelectChangeEvent<unknown>): void => {
    const identifiersArray: number[] = [];
    keys(checkedCtx.checked).forEach(key => {
      identifiersArray.push(Number(key));
    });
    if (identifiersArray.length && props.bulkActionHandler) {
      props.bulkActionHandler(event.target.value, identifiersArray);
    }
  };

  const dependencyArray: DependencyList = props.paginationConfig
    ? [
        props.paginationConfig.props.page,
        props.paginationConfig.props.rowsPerPage,
        sortedBy,
      ]
    : [sortedBy];

  useEffect(() => checkedCtx?.setIsChecked({}), dependencyArray);

  useEffect(() => {
    if (props.closeAllRows) {
      setExpandedRows((prevState: boolean[]) => prevState.map(() => false));
    }
  }, [props.closeAllRows]);

  const handleSort = (field: string, isSortable: boolean | undefined): void => {
    if (isSortable) {
      switch (sortedBy[field]) {
        case SortingState.ASCENDING:
          setIsSortedBy(prevState => ({
            ...prevState,
            [field]: SortingState.DESCENDING,
          }));
          break;
        case SortingState.DESCENDING:
          setIsSortedBy({});
          break;
        default:
          setIsSortedBy({
            [field]: SortingState.ASCENDING,
          });
          break;
      }
    }
  };

  const getSortingArrowState = (
    field: string,
    key: string | undefined
  ): ReactElement | undefined => {
    if (field === key) {
      return sortedBy[field] === SortingState.ASCENDING ? (
        <ArrowDropUpIcon />
      ) : (
        <ArrowDropDownIcon />
      );
    }
  };

  const onRowCheck = (rowId: number): void => {
    checkedCtx.setIsChecked(prevState => {
      if (prevState[rowId]) {
        return omit(prevState, rowId.toString());
      } else {
        return {
          ...prevState,
          [rowId]: true,
        };
      }
    });
  };

  const getSelectedLength = (): number => {
    return size(checkedCtx.checked);
  };

  const selectAll = (): void => {
    if (getSelectedLength() === rowsFromProps.length) {
      checkedCtx.setIsChecked({});
      return;
    }

    const checkedState: TableObjectModel<boolean> = {};
    rowsFromProps.forEach(row => {
      checkedState[row.id as number] = true;
    });
    checkedCtx.setIsChecked(checkedState);
  };

  return (
    <ThemeProvider theme={baseTableTheme}>
      <TableContainer component={props.component || 'div'}>
        <div className="table-container" data-testid="sdc-table">
          <Table
            sx={props.sx}
            aria-label={props.ariaLabel}
            stickyHeader={props.stickyHeader}
          >
            <TableHead>
              <TableRow>
                {props.checkboxSelection && (
                  <TableCell
                    align={Alignments.CENTER}
                    key={`checkbox-selection-header-col`}
                  >
                    <Checkbox
                      icon={<UncheckedIcon />}
                      className="table-multiselect-checkbox"
                      indeterminate={
                        getSelectedLength() > 0 &&
                        getSelectedLength() < rowsFromProps.length
                      }
                      checked={getSelectedLength() === rowsFromProps.length}
                      onClick={selectAll}
                      sx={{
                        width: 'auto',
                      }}
                    />
                  </TableCell>
                )}
                {columns.map((column, index) => (
                  <TableCell
                    key={`header-col-${index}`}
                    align={column.alignment}
                    colSpan={column.colSpan}
                    onClick={() => {
                      handleSort(column.field, column.sortable);
                    }}
                    sx={{
                      cursor: column.sortable ? 'pointer' : 'default',
                      ...(column.width && {
                        width: column.width,
                      }),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      {column.headerName}
                      {getSortingArrowState(
                        column.field,
                        Object.keys(sortedBy)[0]
                      )}
                    </Box>
                  </TableCell>
                ))}
                {props.expandableRows && (
                  <TableCell
                    key={`expandable-header-col`}
                    sx={{ width: '25px' }}
                  />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsFromProps.map((row, index) => {
                return (
                  <Fragment key={`fragment-row-${index}`}>
                    {!props.expandableRows && (
                      <TableRow key={`table-row-${index}`}>
                        <TableCells
                          checked={!!checkedCtx?.checked[row.id as number]}
                          columns={columns}
                          index={index}
                          onRowCheck={onRowCheck}
                          row={row}
                          selectionCheckbox={props.checkboxSelection}
                        />
                      </TableRow>
                    )}
                    {props.expandableRows && (
                      // colspan.length + 2 -> checkbox column and expandable column
                      <TableRowExpandable
                        colSpan={
                          [...columns].length +
                          1 +
                          (props.checkboxSelection ? 1 : 0)
                        }
                        expandComponent={props.expandableComponent!({
                          data: row,
                        })}
                        isExpanded={expandedRows[index]}
                        onExpandRow={toggleExpandRow}
                        currentRowIndex={index}
                        changePrompt={props.changePrompt}
                      >
                        <TableCells
                          checked={!!checkedCtx?.checked[row.id as number]}
                          columns={columns}
                          index={index}
                          onRowCheck={onRowCheck}
                          row={row}
                          selectionCheckbox={props.checkboxSelection}
                        />
                      </TableRowExpandable>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {props.paginationConfig && (
          <div
            className={`table-footer${
              props.bulkActions || props.footerElement ? '' : ' no-bulk'
            }`}
          >
            {props.footerElement
              ? props.footerElement
              : props.bulkActions && (
                  <Select
                    value={''}
                    className="bulk-actions-select"
                    classes={{
                      select: 'bulk-actions-list',
                    }}
                    displayEmpty
                    native={false}
                    onChange={bulkActionsHandler}
                  >
                    <MenuItem
                      value={''}
                      disabled={true}
                      sx={{ pointerEvents: 'none', color: tableGrey }}
                    >
                      Bulk Actions
                    </MenuItem>
                    <MenuItem value="delete">Delete</MenuItem>
                  </Select>
                )}

            <TablePagination
              component={'div'}
              className="table-pagination"
              {...props.paginationConfig.props}
            />
          </div>
        )}
      </TableContainer>
    </ThemeProvider>
  );
};

export default SDCTable;
